import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Plus,
  Pencil,
  Trash2,
  ChevronUp,
  ChevronDown,
  Clock,
  Users,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { type PricingTierDraft, getTierName, getTierPriceDisplay } from './types';
import { PricingTierDialog } from './PricingTierDialog';

interface PricingTiersOfflineProps {
  value: PricingTierDraft[];
  onChange: (tiers: PricingTierDraft[]) => void;
  /** Показывать ли секцию семейного тарифа (только для абонементов) */
  showFamily?: boolean;
}

/**
 * PricingTiersOffline
 *
 * Список тарифов, работающий полностью в памяти (без API).
 * Используется в wizard-формах при создании курсов/абонементов.
 */
export function PricingTiersOffline({ value, onChange, showFamily = false }: PricingTiersOfflineProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<PricingTierDraft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PricingTierDraft | null>(null);

  const handleAdd = () => {
    setEditingTier(null);
    setDialogOpen(true);
  };

  const handleEdit = (tier: PricingTierDraft) => {
    setEditingTier(tier);
    setDialogOpen(true);
  };

  const handleSave = (tier: PricingTierDraft) => {
    const exists = value.some((t) => t.tempId === tier.tempId);
    if (exists) {
      onChange(value.map((t) => (t.tempId === tier.tempId ? tier : t)));
    } else {
      onChange([...value, tier]);
    }
  };

  const handleDelete = (tier: PricingTierDraft) => {
    setDeleteTarget(tier);
  };

  const confirmDelete = () => {
    if (!deleteTarget) { return; }
    const remaining = value
      .filter((t) => t.tempId !== deleteTarget.tempId)
      .map((t, i) => ({ ...t, order: i }));
    onChange(remaining);
    setDeleteTarget(null);
  };

  const handleMoveUp = (index: number) => {
    if (index <= 0) { return; }
    const next = [...value];
    const temp = next[index - 1];
    next[index - 1] = { ...next[index], order: index - 1 };
    next[index] = { ...temp, order: index };
    onChange(next);
  };

  const handleMoveDown = (index: number) => {
    if (index >= value.length - 1) { return; }
    const next = [...value];
    const temp = next[index + 1];
    next[index + 1] = { ...next[index], order: index + 1 };
    next[index] = { ...temp, order: index };
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {value.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-muted-foreground text-sm mb-3">
            Тарифы ещё не добавлены
          </p>
          <Button variant="outline" size="sm" onClick={handleAdd}>
            <Plus className="mr-1.5 h-4 w-4" />
            Добавить тариф
          </Button>
        </div>
      ) : (
        <>
          {value.map((tier, index) => (
            <TierCard
              key={tier.tempId}
              tier={tier}
              index={index}
              total={value.length}
              onEdit={() => handleEdit(tier)}
              onDelete={() => handleDelete(tier)}
              onMoveUp={() => handleMoveUp(index)}
              onMoveDown={() => handleMoveDown(index)}
            />
          ))}
          <Button variant="outline" size="sm" onClick={handleAdd} className="w-full">
            <Plus className="mr-1.5 h-4 w-4" />
            Добавить тариф
          </Button>
        </>
      )}

      <PricingTierDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        tier={editingTier}
        order={value.length}
        onSave={handleSave}
        showFamily={showFamily}
      />

      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить тариф?</AlertDialogTitle>
            <AlertDialogDescription>
              Тариф &laquo;{deleteTarget ? getTierName(deleteTarget) : ''}&raquo; будет удалён.
              Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Удалить</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface TierCardProps {
  tier: PricingTierDraft;
  index: number;
  total: number;
  onEdit: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function TierCard({
  tier,
  index,
  total,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
}: TierCardProps) {
  const name = getTierName(tier);
  const priceDisplay = getTierPriceDisplay(tier);

  return (
    <Card
      className={cn(
        'py-3',
        !tier.isActive && 'opacity-60',
      )}
    >
      <CardContent className="flex items-center gap-3 px-4 py-0">
        {/* Reorder buttons */}
        <div className="flex flex-col gap-0.5">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={index <= 0}
            className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
            aria-label="Переместить вверх"
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={index >= total - 1}
            className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
            aria-label="Переместить вниз"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Gradient preview */}
        {tier.gradientFrom && tier.gradientTo && (
          <div
            className="h-8 w-8 shrink-0 rounded-md"
            style={{
              background: `linear-gradient(${tier.gradientAngle}deg, ${tier.gradientFrom}, ${tier.gradientTo})`,
            }}
          />
        )}

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm truncate">{name}</span>
            {!tier.isActive && (
              <Badge variant="secondary" className="text-[10px]">Неактивен</Badge>
            )}
            {tier.isFamily && (
              <Badge variant="outline" className="text-[10px] gap-1">
                <Users className="h-3 w-3" />
                {tier.familySlots}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
            <span>{priceDisplay}</span>
            {tier.durationDays !== null && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {tier.durationDays} дн.
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={onEdit} className="h-7 w-7 p-0">
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} className="h-7 w-7 p-0 text-destructive hover:text-destructive">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
